import { DataProvider } from 'react-admin';
import { v4 as uuidv4 } from 'uuid';
import { UPLOAD_FIELD_NAMES } from '../../../../consts';
import {
	createDirectory,
	deleteFileFromDrive,
	handleFileDelete,
	handleFileUpload
} from './fileUploader';

type MediaGroup = {
	images: File[];
	videos: File[];
	documents: File[];
	layout: File[];
	avatar?: File | null; // or `File | null` depending on your use
};

type DeletedFiles = {
	images: DeleteContext[];
	videos: DeleteContext[];
	documents: DeleteContext[];
	layout: DeleteContext[];
};

export const pendingFileUploads = new Map<string, MediaGroup>();
export const pendingFileDeletions = new Map<string, DeletedFiles>();

export async function handleBeforeCreate(params: any) {
	let images: File[] = [];
	let videos: File[] = [];
	let documents: File[] = [];
	let layout: File[] = [];
	let avatar: File | null = null;
	let avatarID: string | null = null;
	console.log("Before Create params.data:",params.data);
	try {
		if (params.data.avatar?.rawFile) {
			avatar = params.data.avatar.rawFile;
		}

		// Extract images
		if (Array.isArray(params.data.image)) {
			images = params.data.image.map((fileObj: any) => fileObj.rawFile);
		} else if (params.data.image) {
			images = [params.data.image.rawFile];
		}

		// Extract videos
		if (Array.isArray(params.data.video)) {
			videos = params.data.video.map((fileObj: any) => fileObj.rawFile);
		} else if (params.data.video) {
			videos = [params.data.video.rawFile];
		}

		// Extract documents
		if (Array.isArray(params.data.document)) {
			documents = params.data.document.map((fileObj: any) => fileObj.rawFile);
		} else if (params.data.document) {
			documents = [params.data.document.rawFile];
		}

		// Extract layout (single file)
		if (params.data.layout?.rawFile) {
			layout = [params.data.layout.rawFile];
		}

		const apartmentId = uuidv4();

		// Upload avatar
		if (avatar) {
			avatarID = await uploadAvatar(apartmentId, avatar);
		}

		// Store media as an object with separate types
		// @ts-ignore
		pendingFileUploads.set(apartmentId, { images, videos, documents, layout });


		console.log(`📦 [BeforeCreate] Caching media for apartment ${apartmentId}`);
		console.log(
			`🖼️ Images: ${images.length}, 🎥 Videos: ${videos.length}, 📄 Documents: ${documents.length}, 🧩 Layout: ${layout.length}`
		);

		// Remove media-related data from params
		delete params.data.image;
		delete params.data.video;
		delete params.data.document;
		delete params.data.layout;
		delete params.data.avatar;

		const updatedData = {
			...params.data,
			id: apartmentId,
			avatar: avatarID ? { src: avatarID } : undefined,
			status_data: {
				status_id: 1,
				date: new Date().toISOString()
			},
			created_at: new Date().toISOString(),
			updated_at: new Date().toISOString()
		};

		console.log(updatedData);

		return {
			...params,
			data: updatedData
		};
	} catch (err) {
		console.error('❌ [BeforeCreate] Error processing media files:', err);
		throw err;
	}
}

async function uploadAvatar(
	apartmentId: string,
	avatar: File
): Promise<string> {
	try {
		const formData = new FormData();
		formData.append('avatar', avatar);

		const res = await fetch(`/api/upload-avatar/apartments/${apartmentId}`, {
			method: 'POST',
			body: formData
		});

		if (!res.ok) {
			throw new Error(`Avatar upload failed: ${await res.text()}`);
		}

		const avatarResponse = await res.json();
		const driveFileId = avatarResponse.avatar.driveFile.id;

		console.log(
			`🧑‍🎨 [Avatar] Uploaded for apartment ${apartmentId}`,
			avatarResponse
		);

		return driveFileId;
	} catch (error) {
		console.error('❌ [Avatar] Upload failed:', error);
		throw error;
	}
}

export async function handleAfterCreate(
	params: any,
	baseDataProvider: DataProvider
) {
	const { data } = params;
	const apartmentId = data.id;

	try {
		const mediaData = pendingFileUploads.get(apartmentId) || {
			images: [],
			videos: [],
			documents: [],
			layout: []
		};

		const { images, videos, documents, layout } = mediaData;

		console.log(
			`🚀 [AfterCreate] Uploading media for apartment ${apartmentId}: 🖼️ ${images.length}, 🎥 ${videos.length}, 📄 ${documents.length}, 🗺️ ${layout ? '1 layout' : 'no layout'}`
		);
		await createDirectory(`apartments-${apartmentId}`);
		// Upload media
		await handleFileUpload(
			images,
			`apartments-${apartmentId}`,
			baseDataProvider,
			UPLOAD_FIELD_NAMES.image
		);
		await handleFileUpload(
			videos,
			`apartments-${apartmentId}`,
			baseDataProvider,
			UPLOAD_FIELD_NAMES.video
		);
		await handleFileUpload(
			documents,
			`apartments-${apartmentId}`,
			baseDataProvider,
			UPLOAD_FIELD_NAMES.document
		);
		await handleFileUpload(
			layout,
			`apartments-${apartmentId}`,
			baseDataProvider,
			UPLOAD_FIELD_NAMES.layout
		);
		// Clean up
		pendingFileUploads.delete(apartmentId);

		console.log(`🧹 [AfterCreate] Cache cleared for apartment ${apartmentId}`);

		return params;
	} catch (err) {
		console.error(
			`❌ [AfterCreate] Media upload failed for apartment ${apartmentId}:`,
			err
		);
		throw err;
	}
}

export const handleAfterGetOne =
	(baseDataProvider: DataProvider) => async (params: any) => {
		try {
			const [images, videos, layouts, documents] = await Promise.all([
				baseDataProvider.getList('images', {
					filter: { property_id: params.data.id },
					pagination: { page: 1, perPage: 10000 },
					sort: { field: 'id', order: 'ASC' }
				}),
				baseDataProvider.getList('videos', {
					filter: { property_id: params.data.id },
					pagination: { page: 1, perPage: 10000 },
					sort: { field: 'id', order: 'ASC' }
				}),
				baseDataProvider.getList('layout', {
					filter: { property_id: params.data.id },
					pagination: { page: 1, perPage: 1 }, // Assuming only one layout
					sort: { field: 'id', order: 'ASC' }
				}),
				baseDataProvider.getList('documents', {
					filter: { property_id: params.data.id },
					pagination: { page: 1, perPage: 10000 },
					sort: { field: 'id', order: 'ASC' }
				})
			]);

			console.log(`📷 [AfterGetOne] Images: ${images.data.length}`);
			console.log(`🎥 [AfterGetOne] Videos: ${videos.data.length}`);
			console.log(`📐 [AfterGetOne] Layouts: ${layouts.data.length}`);
			console.log(`📄 [AfterGetOne] Documents: ${documents.data.length}`);

			const mappedImages = images.data.map(file => ({
				title: file.title,
				src: `/api/media/${file.file_path}`,
				filePath: file.file_path,
				id: file.id
			}));

			const mappedVideos = videos.data.map(file => ({
				title: file.title,
				src: `/api/media/${file.file_path}`,
				filePath: file.file_path,
				id: file.id
			}));

			const layoutImage = layouts.data.length
				? {
						title: layouts.data[0].title,
						src: `/api/media/${layouts.data[0].file_path}`,
						id: layouts.data[0].id,
						filePath: layouts.data[0].file_path
					}
				: null;

			const mappedDocuments = documents.data.map(file => ({
				title: file.title,
				src: `/api/media/${file.file_path}`,
				filePath: file.file_path,
				id: file.id
			}));
			console.log('AfterGetOne params.data.avatar?.src', params.data.avatar);
			let avatar = null;
			if (null !== params.data.avatar) {
				avatar = { src: `/api/media/${params.data.avatar?.src}` };
			}
			delete params.data.avatar;

			return {
				...params,
				data: {
					...params.data,
					image: mappedImages,
					video: mappedVideos,
					layout: layoutImage,
					document: mappedDocuments,
					avatar: avatar ?? null
				}
			};
		} catch (err) {
			console.error(
				`❌ [AfterGetOne] Error fetching media for apartment ${params.data.id}:`,
				err
			);
			throw err;
		}
	};

interface DeleteContext {
	recordId: string;
	baseDataProvider: DataProvider;
	type: UPLOAD_FIELD_NAMES;
	filePath: string;
}

function simplifyAvatar(avatar: { title: string }): { src: string } {
	if (typeof avatar.title !== 'string') return { src: '' };
	const parts = avatar.title.split('/');
	return { src: parts[parts.length - 1] };
}

export async function handleBeforeUpdate(params: any) {
	let images: File[] = [];
	let videos: File[] = [];
	let documents: File[] = [];
	let layout: File[] = [];

	const apartmentId = params.id;

	const deletedFiles: DeletedFiles = {
		images: [],
		videos: [],
		documents: [],
		layout: []
	};
	let avatarID;
	if (params.data.avatar && params.previousData) {
		const previousAvatarSrc = params.previousData.avatar?.src;
		console.log('previousAvatarSrc', previousAvatarSrc);
		console.log('current avatar SRC', params.data.avatar);
		if (previousAvatarSrc! == null && params.data.avatar.rawFile.size > 0) {
			await deleteFileFromDrive(previousAvatarSrc);
		}
		if (params.data.avatar && params.data.avatar.rawFile.size > 0) {
			avatarID = await uploadAvatar(apartmentId, params.data.avatar.rawFile);
		}
	}

	const pushDeleted = (
		files: { id: string; filePath: string }[],
		type: UPLOAD_FIELD_NAMES,
		target: any[]
	) => {
		for (const file of files) {
			console.log('pushDeleted > file', file);
			target.push({
				recordId: file.id,
				type,
				filePath: file.filePath
			});
		}
	};

	try {
		const oldImages = params.previousData?.image || [];
		const oldVideos = params.previousData?.video || [];
		const oldDocuments = params.previousData?.document || [];
		const oldLayout = params.previousData?.layout;

		// === IMAGES ===
		if (Array.isArray(params.data.image)) {
			const newImageIds = params.data.image
				.filter((f: any) => !f.rawFile)
				.map((f: any) => f.id);
			const removed = oldImages.filter(
				(old: any) => !newImageIds.includes(old.id)
			);
			pushDeleted(removed, UPLOAD_FIELD_NAMES.image, deletedFiles.images);
			images = params.data.image
				.filter((f: any) => f.rawFile)
				.map((f: any) => f.rawFile);
		} else if (params.data.image?.rawFile) {
			pushDeleted(oldImages, UPLOAD_FIELD_NAMES.image, deletedFiles.images);
			images = [params.data.image.rawFile];
		}

		// === VIDEOS ===
		if (Array.isArray(params.data.video)) {
			const newIds = params.data.video
				.filter((f: any) => !f.rawFile)
				.map((f: any) => f.id);
			const removed = oldVideos.filter((old: any) => !newIds.includes(old.id));
			pushDeleted(removed, UPLOAD_FIELD_NAMES.video, deletedFiles.videos);
			videos = params.data.video
				.filter((f: any) => f.rawFile)
				.map((f: any) => f.rawFile);
		} else if (params.data.video?.rawFile) {
			pushDeleted(oldVideos, UPLOAD_FIELD_NAMES.video, deletedFiles.videos);
			videos = [params.data.video.rawFile];
		}

		// === DOCUMENTS ===
		if (Array.isArray(params.data.document)) {
			const newIds = params.data.document
				.filter((f: any) => !f.rawFile)
				.map((f: any) => f.id);
			const removed = oldDocuments.filter(
				(old: any) => !newIds.includes(old.id)
			);
			pushDeleted(removed, UPLOAD_FIELD_NAMES.document, deletedFiles.documents);
			documents = params.data.document
				.filter((f: any) => f.rawFile)
				.map((f: any) => f.rawFile);
		} else if (params.data.document?.rawFile) {
			pushDeleted(
				oldDocuments,
				UPLOAD_FIELD_NAMES.document,
				deletedFiles.documents
			);
			documents = [params.data.document.rawFile];
		}

		// === LAYOUT ===
		if (params.data.layout?.rawFile instanceof File) {
			if (oldLayout?.id && oldLayout?.filePath) {
				pushDeleted(
					[oldLayout],
					UPLOAD_FIELD_NAMES.layout,
					deletedFiles.layout
				);
			}
			layout = [params.data.layout.rawFile];
		} else if (
			params.data.layout?.id &&
			oldLayout?.id &&
			params.data.layout.id !== oldLayout.id
		) {
			pushDeleted([oldLayout], UPLOAD_FIELD_NAMES.layout, deletedFiles.layout);
		} else if (!params.data.layout && oldLayout?.id) {
			pushDeleted([oldLayout], UPLOAD_FIELD_NAMES.layout, deletedFiles.layout);
		}

		// === Cache uploads ===
		if (images.length || videos.length || documents.length || layout.length) {
			pendingFileUploads.set(apartmentId, {
				images,
				videos,
				documents,
				layout
			});
			console.log(`♻️ [BeforeUpdate] Caching new files for ${apartmentId}`);
		}

		// === Cache deletions ===
		if (
			deletedFiles.images.length ||
			deletedFiles.videos.length ||
			deletedFiles.documents.length ||
			deletedFiles.layout.length
		) {
			pendingFileDeletions.set(apartmentId, deletedFiles);
			console.log(
				`🗑 [BeforeUpdate] Marking files for removal for ${apartmentId}:`,
				deletedFiles
			);
		}
		let previousAvatar = params.data.avatar
			? simplifyAvatar(params.data.avatar)
			: null;
		// === Clean file fields ===
		delete params.data.image;
		delete params.data.video;
		delete params.data.document;
		delete params.data.layout;
		delete params.data.avatar;

		return {
			...params,
			data: {
				...params.data,
				avatar: avatarID ? { src: avatarID } : previousAvatar,
				updated_at: new Date().toISOString()
			}
		};
	} catch (err) {
		console.error('❌ [BeforeUpdate] Error comparing media:', err);
		throw err;
	}
}

export async function handleAfterUpdate(
	params: any,
	baseDataProvider: DataProvider
) {
	const { data } = params;
	const apartmentId = data.id;
	console.log('handleAfterUpdate > data', data);

	try {
		const mediaData = pendingFileUploads.get(apartmentId) || {
			images: [],
			videos: [],
			documents: [],
			layout: []
		};

		const filesToDelete = pendingFileDeletions.get(apartmentId) || {
			images: [] as DeleteContext[],
			videos: [] as DeleteContext[],
			documents: [] as DeleteContext[],
			layout: [] as DeleteContext[]
		};

		console.log('filesToDelete', filesToDelete);
		// console.log('rawDeletions', rawDeletions);

		const { images, videos, documents, layout } = mediaData;
		const allFilesToDelete = [
			...filesToDelete.images,
			...filesToDelete.videos,
			...filesToDelete.documents,
			...filesToDelete.layout
		];

		console.log(
			`🔁 [AfterUpdate] Processing apartment ${apartmentId}:
			Uploading: 🖼️ ${images.length}, 🎥 ${videos.length}, 📄 ${documents.length}, 🧩 ${layout.length}
			Deleting: 🗑️ ${allFilesToDelete.length}`
		);

		await createDirectory(`apartments-${apartmentId}`);

		// Upload new media
		await handleFileUpload(
			images,
			`apartments-${apartmentId}`,
			baseDataProvider,
			UPLOAD_FIELD_NAMES.image
		);
		await handleFileUpload(
			videos,
			`apartments-${apartmentId}`,
			baseDataProvider,
			UPLOAD_FIELD_NAMES.video
		);
		await handleFileUpload(
			documents,
			`apartments-${apartmentId}`,
			baseDataProvider,
			UPLOAD_FIELD_NAMES.document
		);
		await handleFileUpload(
			layout,
			`apartments-${apartmentId}`,
			baseDataProvider,
			UPLOAD_FIELD_NAMES.layout
		);

		// Delete removed files
		await handleFileDelete(allFilesToDelete, baseDataProvider);

		// Clean up
		pendingFileUploads.delete(apartmentId);
		pendingFileDeletions.delete(apartmentId);

		console.log(
			`🧹 [AfterUpdate] Cleanup complete for apartment ${apartmentId}`
		);

		return params;
	} catch (err) {
		console.error(
			`❌ [AfterUpdate] Failed to process media for apartment ${apartmentId}:`,
			err
		);
		throw err;
	}
}
